"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import useToggleState from "@lib/hooks/use-toggle-state"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import {
  OUR_SERVICES,
  serviceMenuHref,
} from "@lib/content/services"
import { Locale } from "@lib/data/locales"
import { t } from "@lib/i18n"
import { normalizeLocale } from "@lib/i18n/locales"
import type { MenuBrand } from "@lib/util/home-categories"
import type { NavSegment } from "@lib/util/nav-menu"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Text, clx } from "@modules/common/components/ui"
import { Fragment, useMemo, useState } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  brands?: MenuBrand[]
  segments?: NavSegment[]
  year: number
}

const SideMenu = ({
  regions,
  locales,
  currentLocale,
  brands = [],
  segments = [],
  year,
}: SideMenuProps) => {
  const locale = normalizeLocale(currentLocale)
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()
  const [shopOpen, setShopOpen] = useState(true)
  const [contactOpen, setContactOpen] = useState(true)
  const [expandedBrandId, setExpandedBrandId] = useState<string | null>(
    brands[0]?.id ?? null
  )
  const [expandedSegmentId, setExpandedSegmentId] = useState<string | null>(
    null
  )

  const beforeContact = useMemo(
    () => [
      { name: t(locale, "nav.home"), href: "/", testId: "home-link" },
      {
        name: t(locale, "nav.solutions"),
        href: "/solutions",
        testId: "solutions-link",
      },
      { name: t(locale, "nav.guides"), href: "/guides", testId: "guides-link" },
      { name: t(locale, "nav.shop"), href: "/store", testId: "store-link" },
      { name: t(locale, "nav.used"), href: "/used", testId: "used-link" },
      {
        name: t(locale, "nav.ecosystem"),
        href: "/ecosystem",
        testId: "ecosystem-link",
      },
    ],
    [locale]
  )

  const afterContact = useMemo(
    () => [
      {
        name: t(locale, "nav.account"),
        href: "/account",
        testId: "account-link",
      },
      { name: t(locale, "nav.cart"), href: "/cart", testId: "cart-link" },
    ],
    [locale]
  )

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative flex min-h-11 min-w-11 items-center justify-center text-[14px] font-medium text-vx-ink/80 transition-all duration-200 ease-out focus:outline-none hover:text-vx-ink"
                  aria-label={t(locale, "nav.menu")}
                >
                  {t(locale, "nav.menu")}
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/60 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="absolute inset-x-0 z-[51] m-2 flex h-[calc(100dvh-1rem)] flex-col text-sm text-vx-ink small:inset-x-auto small:left-0 small:w-[min(420px,calc(100vw-2rem))] small:pr-0 2xl:w-1/4">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex h-full flex-col justify-between border border-vx-line bg-vx-surface p-5 xsmall:p-6"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button
                        data-testid="close-menu-button"
                        onClick={close}
                        className="grid min-h-11 min-w-11 place-items-center text-vx-muted hover:text-vx-ink"
                        aria-label="Close navigation menu"
                      >
                        <XMark />
                      </button>
                    </div>
                    <div className="flex flex-col gap-5 overflow-y-auto">
                      <ul className="flex flex-col gap-3 items-start justify-start">
                        {beforeContact.map((item) => (
                          <li key={item.href}>
                            <LocalizedClientLink
                              href={item.href}
                              className="text-[17px] leading-8 text-vx-ink/70 hover:text-vx-ink transition-colors"
                              onClick={close}
                              data-testid={item.testId}
                            >
                              {item.name}
                            </LocalizedClientLink>
                          </li>
                        ))}
                        <li className="w-full">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between text-[17px] leading-8 text-vx-ink/70 hover:text-vx-ink"
                            aria-expanded={contactOpen}
                            onClick={() => setContactOpen((value) => !value)}
                            data-testid="menu-contact-section-toggle"
                          >
                            {t(locale, "nav.contact")}
                            <ArrowRightMini
                              className={clx(
                                "transition-transform duration-150",
                                contactOpen ? "-rotate-90" : ""
                              )}
                            />
                          </button>
                          {contactOpen && (
                            <ul className="mt-1 ml-3 flex flex-col gap-2 border-l border-vx-line pl-3">
                              <li>
                                <LocalizedClientLink
                                  href="/contact"
                                  className="text-[14px] text-vx-accent hover:text-vx-ink"
                                  onClick={close}
                                  data-testid="contact-us-link"
                                >
                                  {t(locale, "nav.contact")}
                                </LocalizedClientLink>
                              </li>
                              <li className="pt-1">
                                <p className="text-[13px] font-semibold text-vx-ink">
                                  {t(locale, "nav.contact")}
                                </p>
                                <ul className="mt-1.5 flex flex-col gap-1.5">
                                  {OUR_SERVICES.map((service) => (
                                    <li key={service.slug}>
                                      <LocalizedClientLink
                                        href={serviceMenuHref(service)}
                                        className="text-[14px] text-vx-ink/70 hover:text-vx-ink"
                                        onClick={close}
                                        data-testid={`menu-service-${service.slug}`}
                                      >
                                        {service.label}
                                      </LocalizedClientLink>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            </ul>
                          )}
                        </li>
                        {afterContact.map((item) => (
                          <li key={item.href}>
                            <LocalizedClientLink
                              href={item.href}
                              className="text-[17px] leading-8 text-vx-ink/70 hover:text-vx-ink transition-colors"
                              onClick={close}
                              data-testid={item.testId}
                            >
                              {item.name}
                            </LocalizedClientLink>
                          </li>
                        ))}
                      </ul>

                      {(brands.length > 0 || segments.length > 0) && (
                        <div>
                          <button
                            type="button"
                            className="mb-2 flex w-full items-center justify-between text-[16px] font-semibold text-vx-ink"
                            aria-expanded={shopOpen}
                            onClick={() => setShopOpen((value) => !value)}
                            data-testid="menu-shop-section-toggle"
                          >
                            {t(locale, "nav.shop")}
                            <ArrowRightMini
                              className={clx(
                                "transition-transform duration-150",
                                shopOpen ? "-rotate-90" : ""
                              )}
                            />
                          </button>
                          {shopOpen && (
                            <div className="flex flex-col gap-4">
                              {brands.length > 0 && (
                                <ul className="flex flex-col gap-1">
                                  {brands.map((brand) => {
                                    const isExpanded =
                                      expandedBrandId === brand.id
                                    return (
                                      <li key={brand.id}>
                                        <button
                                          type="button"
                                          className="flex min-h-11 w-full items-center justify-between px-1 text-left text-[17px] text-vx-ink hover:text-vx-accent"
                                          aria-expanded={isExpanded}
                                          onClick={() =>
                                            setExpandedBrandId(
                                              isExpanded ? null : brand.id
                                            )
                                          }
                                          data-testid={`menu-brand-toggle-${brand.handle}`}
                                        >
                                          {brand.name}
                                          <ArrowRightMini
                                            className={clx(
                                              "shrink-0 text-vx-muted transition-transform duration-150",
                                              isExpanded ? "-rotate-90" : ""
                                            )}
                                          />
                                        </button>
                                        {isExpanded && (
                                          <ul className="mb-2 ml-3 flex flex-col gap-2 border-l border-vx-line pl-3">
                                            <li>
                                              <LocalizedClientLink
                                                href={`/categories/${brand.handle}`}
                                                className="text-[14px] text-vx-accent hover:text-vx-ink"
                                                onClick={close}
                                                data-testid={`menu-brand-${brand.handle}`}
                                              >
                                                {brand.name}
                                              </LocalizedClientLink>
                                            </li>
                                            {brand.lines.map((line) => (
                                              <li key={line.id}>
                                                <LocalizedClientLink
                                                  href={`/categories/${line.handle}`}
                                                  className="text-[14px] text-vx-ink/70 hover:text-vx-ink"
                                                  onClick={close}
                                                  data-testid={`menu-line-${line.handle}`}
                                                >
                                                  {line.name}
                                                </LocalizedClientLink>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </li>
                                    )
                                  })}
                                </ul>
                              )}

                              {segments.map((segment) => {
                                const isOpen = expandedSegmentId === segment.id
                                return (
                                  <div key={segment.id}>
                                    <button
                                      type="button"
                                      className="mb-2 flex w-full items-center justify-between text-[15px] font-semibold text-vx-ink"
                                      aria-expanded={isOpen}
                                      onClick={() =>
                                        setExpandedSegmentId(
                                          isOpen ? null : segment.id
                                        )
                                      }
                                      data-testid={`menu-segment-${segment.id}`}
                                    >
                                      {segment.label}
                                      <ArrowRightMini
                                        className={clx(
                                          "transition-transform duration-150",
                                          isOpen ? "-rotate-90" : ""
                                        )}
                                      />
                                    </button>
                                    {isOpen && (
                                      <ul className="flex flex-col gap-3">
                                        {segment.columns.map((column) => (
                                          <li key={column.id}>
                                            <LocalizedClientLink
                                              href={column.href}
                                              className="block px-1 text-[15px] font-medium text-vx-ink hover:text-vx-accent"
                                              onClick={close}
                                            >
                                              {column.name}
                                            </LocalizedClientLink>
                                            <ul className="mt-1.5 ml-3 flex flex-col gap-1.5 border-l border-vx-line pl-3">
                                              {column.links.map((link) => (
                                                <li key={link.handle}>
                                                  <LocalizedClientLink
                                                    href={link.href}
                                                    className="text-[14px] text-vx-ink/70 hover:text-vx-ink"
                                                    onClick={close}
                                                  >
                                                    {link.label}
                                                  </LocalizedClientLink>
                                                </li>
                                              ))}
                                            </ul>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-y-6 text-vx-muted">
                      {!!locales?.length && (
                        <div
                          className="flex justify-between"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}
                      <div
                        className="flex justify-between"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text className="flex justify-between txt-compact-small text-vx-muted">
                        © {year} VX Work. All rights reserved.
                      </Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
